import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import {
  coachSessionsEndpoints,
  ICoachSessionListItem,
  ICreateCoachSessionBody,
  SessionObjective,
} from '../../endpoints/coachSessions';

dayjs.extend(isoWeek);

export type ViewMode = 'week' | 'month';

export const OBJECTIVE_COLORS: Record<string, string> = {
  tactical: '#063444',
  physical: '#f5b350',
  technical: '#7c5cbf',
  recovery: '#4dd292',
  match_prep: '#00a8b5',
  mixed: '#b5b5b5',
};

export const OBJECTIVE_LABELS: Record<string, string> = {
  tactical: 'Tattico',
  physical: 'Fisico',
  technical: 'Tecnico',
  recovery: 'Recovery',
  match_prep: 'Match Prep',
  mixed: 'Misto',
};

export function useCoachSessionWeekView(teamId: string) {
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [referenceDate, setReferenceDate] = useState(() => dayjs());

  // ─── week bounds ──────────────────────────────────────────────────────────
  const weekStart = referenceDate.startOf('isoWeek'); // Monday
  const weekEnd = referenceDate.endOf('isoWeek');     // Sunday

  // ─── month bounds ─────────────────────────────────────────────────────────
  const monthStart = referenceDate.startOf('month');
  const monthEnd = referenceDate.endOf('month');

  const queryStart =
    viewMode === 'week'
      ? weekStart.format('YYYY-MM-DD')
      : monthStart.format('YYYY-MM-DD');

  const queryEnd =
    viewMode === 'week'
      ? weekEnd.format('YYYY-MM-DD')
      : monthEnd.format('YYYY-MM-DD');

  // ─── fetch ────────────────────────────────────────────────────────────────
  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['coachSessions', teamId, queryStart, queryEnd],
    queryFn: () =>
      coachSessionsEndpoints
        .list({ teamId, startDate: queryStart, endDate: queryEnd })
        .then((r) => r.data),
    enabled: !!teamId,
  });

  // ─── mutations ────────────────────────────────────────────────────────────
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['coachSessions', teamId] });
  }, [queryClient, teamId]);

  const createMut = useMutation({
    mutationFn: (body: ICreateCoachSessionBody) =>
      coachSessionsEndpoints.create(body).then((r) => r.data),
    onSuccess: invalidate,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      coachSessionsEndpoints.delete(id).then((r) => r.data),
    onSuccess: invalidate,
  });

  // ─── navigation ───────────────────────────────────────────────────────────
  const prevPeriod = useCallback(() => {
    setReferenceDate((d) =>
      viewMode === 'week' ? d.subtract(1, 'week') : d.subtract(1, 'month'),
    );
  }, [viewMode]);

  const nextPeriod = useCallback(() => {
    setReferenceDate((d) =>
      viewMode === 'week' ? d.add(1, 'week') : d.add(1, 'month'),
    );
  }, [viewMode]);

  const goToToday = useCallback(() => setReferenceDate(dayjs()), []);

  const goToDate = useCallback((iso: string) => {
    setReferenceDate(dayjs(iso));
  }, []);

  // ─── week days ────────────────────────────────────────────────────────────
  const weekDays = Array.from({ length: 7 }, (_, i) =>
    weekStart.add(i, 'day'),
  );

  // ─── sessions by date map ─────────────────────────────────────────────────
  const sessionsByDate = sessions.reduce<
    Record<string, ICoachSessionListItem[]>
  >((acc, s) => {
    const key = dayjs(s.scheduledDate).format('YYYY-MM-DD');
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {});

  // ─── month calendar weeks ─────────────────────────────────────────────────
  const monthCalendarDays = (() => {
    const start = monthStart.startOf('isoWeek');
    const end = monthEnd.endOf('isoWeek');
    const days: dayjs.Dayjs[] = [];
    let cur = start;
    while (cur.isBefore(end) || cur.isSame(end, 'day')) {
      days.push(cur);
      cur = cur.add(1, 'day');
    }
    return days;
  })();

  // ─── label helpers ────────────────────────────────────────────────────────
  const weekLabel = `${weekStart.format('D MMM')} – ${weekEnd.format('D MMM YYYY')}`;
  const monthLabel = referenceDate.format('MMMM YYYY');

  // ─── totals ───────────────────────────────────────────────────────────────
  const weeklyTotals = weekDays.reduce(
    (acc, d) => {
      const key = d.format('YYYY-MM-DD');
      const daySessions = sessionsByDate[key] ?? [];
      daySessions.forEach((s) => {
        acc.duration += s.metrics?.totalDurationMinutes ?? 0;
        acc.ewt += s.metrics?.ewtMinutes ?? 0;
        acc.ce += s.metrics?.totalCE ?? 0;
      });
      return acc;
    },
    { duration: 0, ewt: 0, ce: 0 },
  );

  return {
    viewMode,
    setViewMode,
    referenceDate,
    weekDays,
    weekLabel,
    monthLabel,
    sessions,
    sessionsByDate,
    monthCalendarDays,
    monthStart,
    isLoading,
    prevPeriod,
    nextPeriod,
    goToToday,
    goToDate,
    createMut,
    deleteMut,
    weeklyTotals,
    today: dayjs(),
    OBJECTIVE_COLORS,
    OBJECTIVE_LABELS,
  };
}
